import React, { useEffect, useMemo, useState } from 'react';
import { coffeeProfileLevelService } from '../../../services/api';
import {
  CoffeeProfileLevel,
  CoffeeProfileLevelTranslation,
  CreateCoffeeProfileLevelDto,
  UpdateCoffeeProfileLevelDto,
} from '../../../types/coffeeProfileLevels';

type CoffeeProfileLevelFormState = {
  key: string;
  icon: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  translations: {
    pt: CoffeeProfileLevelTranslation;
    en: CoffeeProfileLevelTranslation;
    es: CoffeeProfileLevelTranslation;
  };
};

const emptyTranslations = (): CoffeeProfileLevelFormState['translations'] => ({
  pt: { name: '', description: '' },
  en: { name: '', description: '' },
  es: { name: '', description: '' },
});

const createEmptyForm = (): CoffeeProfileLevelFormState => ({
  key: '',
  icon: '',
  color: '#C86B35',
  sortOrder: 0,
  isActive: true,
  translations: emptyTranslations(),
});

const cloneTranslations = (translations?: CoffeeProfileLevel['translations']): CoffeeProfileLevelFormState['translations'] => ({
  pt: {
    name: translations?.pt?.name ?? '',
    description: translations?.pt?.description ?? '',
  },
  en: {
    name: translations?.en?.name ?? translations?.pt?.name ?? '',
    description: translations?.en?.description ?? translations?.pt?.description ?? '',
  },
  es: {
    name: translations?.es?.name ?? translations?.pt?.name ?? '',
    description: translations?.es?.description ?? translations?.pt?.description ?? '',
  },
});

const CoffeeProfileLevelsPage = () => {
  const [items, setItems] = useState<CoffeeProfileLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CoffeeProfileLevel | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [formData, setFormData] = useState<CoffeeProfileLevelFormState>(createEmptyForm());

  useEffect(() => {
    void loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await coffeeProfileLevelService.getAllCoffeeProfileLevels();
      setItems(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Erro ao carregar coffee profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData(createEmptyForm());
    setIsModalOpen(true);
  };

  const openEdit = (item: CoffeeProfileLevel) => {
    setEditingItem(item);
    setFormData({
      key: item.key,
      icon: item.icon,
      color: item.color,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
      translations: cloneTranslations(item.translations),
    });
    setIsModalOpen(true);
  };

  const validateForm = (): string | null => {
    if (!formData.key.trim()) {
      return 'A key é obrigatória.';
    }

    if (!formData.translations.pt.name.trim()) {
      return 'O nome em PT é obrigatório.';
    }

    const normalizedKey = formData.key.trim().toLowerCase();
    const duplicated = items.some((item) =>
      item.key.toLowerCase() === normalizedKey && item.id !== editingItem?.id
    );

    if (duplicated) {
      return 'Já existe um coffee profile com essa key.';
    }

    return null;
  };

  const buildDto = (): CreateCoffeeProfileLevelDto | UpdateCoffeeProfileLevelDto => ({
    key: formData.key.trim().toLowerCase(),
    icon: formData.icon.trim(),
    color: formData.color,
    sortOrder: formData.sortOrder,
    name: formData.translations.pt.name.trim(),
    description: formData.translations.pt.description?.trim() || '',
    translations: {
      pt: {
        name: formData.translations.pt.name.trim(),
        description: formData.translations.pt.description?.trim() || '',
      },
      en: {
        name: formData.translations.en.name.trim() || formData.translations.pt.name.trim(),
        description: formData.translations.en.description?.trim() || formData.translations.pt.description?.trim() || '',
      },
      es: {
        name: formData.translations.es.name.trim() || formData.translations.pt.name.trim(),
        description: formData.translations.es.description?.trim() || formData.translations.pt.description?.trim() || '',
      },
    },
    ...(editingItem ? { isActive: formData.isActive } : {}),
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError('');
      setSuccessMessage('');

      if (editingItem) {
        await coffeeProfileLevelService.updateCoffeeProfileLevel(editingItem.id, buildDto() as UpdateCoffeeProfileLevelDto);
        setSuccessMessage('Coffee profile atualizado com sucesso!');
      } else {
        await coffeeProfileLevelService.createCoffeeProfileLevel(buildDto() as CreateCoffeeProfileLevelDto);
        setSuccessMessage('Coffee profile criado com sucesso!');
      }

      setIsModalOpen(false);
      await loadItems();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Erro ao salvar coffee profile');
    }
  };

  const handleToggleStatus = async (item: CoffeeProfileLevel) => {
    try {
      setError('');
      setSuccessMessage('');
      await coffeeProfileLevelService.updateCoffeeProfileLevel(item.id, {
        key: item.key,
        icon: item.icon,
        color: item.color,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
        name: item.translations?.pt?.name ?? item.name,
        description: item.translations?.pt?.description ?? item.description,
        translations: {
          pt: {
            name: item.translations?.pt?.name ?? item.name,
            description: item.translations?.pt?.description ?? item.description,
          },
          en: {
            name: item.translations?.en?.name ?? item.name,
            description: item.translations?.en?.description ?? item.description,
          },
          es: {
            name: item.translations?.es?.name ?? item.name,
            description: item.translations?.es?.description ?? item.description,
          },
        },
      });
      setSuccessMessage(`Coffee profile ${item.isActive ? 'desativado' : 'ativado'} com sucesso!`);
      await loadItems();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(term) ||
        item.key.toLowerCase().includes(term) ||
        (item.description || '').toLowerCase().includes(term);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && item.isActive) ||
        (statusFilter === 'inactive' && !item.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [items, searchTerm, statusFilter]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Coffee Profiles</h1>
          <p className="text-gray-600 mt-2">Gerenciar níveis de perfil do café</p>
        </div>
        <div className="flex justify-center py-8">
          <div className="spinner w-8 h-8" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Coffee Profiles</h1>
          <p className="text-gray-600 mt-2">Gerencie key, ordem e traduções PT / EN / ES</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary inline-flex items-center justify-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo profile
        </button>
      </div>

      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div> : null}
      {successMessage ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
          {successMessage}
        </div>
      ) : null}

      <div className="card">
        <div className="card-body grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Nome, key ou descrição..."
              className="input"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
              className="input"
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            Prefira desativar em vez de excluir. O backend bloqueia remoções quando o profile já está em uso.
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {filteredItems.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="mb-2 text-lg text-gray-500">
                {searchTerm || statusFilter !== 'all' ? 'Nenhum profile encontrado' : 'Nenhum profile cadastrado'}
              </div>
              <p className="text-gray-400">
                {searchTerm || statusFilter !== 'all'
                  ? 'Ajuste os filtros para localizar o item'
                  : 'Clique em "Novo profile" para começar'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Profile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ordem</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-white"
                            style={{ backgroundColor: item.color }}
                          >
                            {item.icon?.slice(0, 1)?.toUpperCase() || 'C'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.description || 'Sem descrição'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.key}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.sortOrder}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {item.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(item)}
                            className="rounded p-1 text-indigo-600 hover:text-indigo-900"
                            title="Editar"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => void handleToggleStatus(item)}
                            className={`rounded p-1 ${
                              item.isActive ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
                            }`}
                            title={item.isActive ? 'Desativar' : 'Ativar'}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {item.isActive ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              )}
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 py-6 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75" aria-hidden="true" onClick={() => setIsModalOpen(false)} />
            <div className="inline-block w-full max-w-3xl transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
              <form onSubmit={handleSubmit}>
                <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
                    {editingItem ? 'Editar profile' : 'Novo profile'}
                  </h3>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Key *</label>
                      <input
                        type="text"
                        value={formData.key}
                        onChange={(event) => setFormData((prev) => ({ ...prev, key: event.target.value }))}
                        className="input"
                        placeholder="coffee_lover"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Ordem</label>
                      <input
                        type="number"
                        value={formData.sortOrder}
                        onChange={(event) => setFormData((prev) => ({ ...prev, sortOrder: Number(event.target.value) || 0 }))}
                        className="input"
                        min={0}
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Ícone</label>
                      <input
                        type="text"
                        value={formData.icon}
                        onChange={(event) => setFormData((prev) => ({ ...prev, icon: event.target.value }))}
                        className="input"
                        placeholder="heart-outline"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">Cor</label>
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(event) => setFormData((prev) => ({ ...prev, color: event.target.value }))}
                        className="h-10 w-full rounded-lg border border-gray-300 p-1"
                      />
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <input
                      id="coffee-profile-active"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="coffee-profile-active" className="text-sm font-medium text-gray-700">
                      Profile ativo
                    </label>
                  </div>

                  <div className="mt-6 space-y-6">
                    {[
                      ['pt', 'Português'],
                      ['en', 'English'],
                      ['es', 'Español'],
                    ].map(([locale, title]) => (
                      <div key={locale} className="rounded-xl border border-gray-200 p-4">
                        <div className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</div>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">Nome{locale === 'pt' ? ' *' : ''}</label>
                            <input
                              type="text"
                              value={formData.translations[locale as 'pt' | 'en' | 'es'].name}
                              onChange={(event) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  translations: {
                                    ...prev.translations,
                                    [locale]: {
                                      ...prev.translations[locale as 'pt' | 'en' | 'es'],
                                      name: event.target.value,
                                    },
                                  },
                                }))
                              }
                              className="input"
                              placeholder="Nome do profile"
                            />
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">Descrição</label>
                            <textarea
                              value={formData.translations[locale as 'pt' | 'en' | 'es'].description || ''}
                              onChange={(event) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  translations: {
                                    ...prev.translations,
                                    [locale]: {
                                      ...prev.translations[locale as 'pt' | 'en' | 'es'],
                                      description: event.target.value,
                                    },
                                  },
                                }))
                              }
                              className="input min-h-[88px]"
                              rows={3}
                              placeholder="Descrição do profile"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 bg-gray-50 px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingItem ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CoffeeProfileLevelsPage;
